import { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useStyles } from '../../../shared/PolicyDialog/styles';
import { LuiSelect } from '@jda/lui-common-component-library-mui5';
import { Grid, TextField, Typography, FormControlLabel, Checkbox, Box } from '@mui/material';
import { ErrorMessage } from '../../../shared/ErrorMessage/ErrorMessage';
import {
  POLICY_NAME,
  POLICY_NAME_LABEL,
  POLICY_DESCRIPTION,
  POLICY_DESC_LABEL,
  POLICY_DIMENSION,
  POLICY_HIERARCHY,
  POLICY_ACTIONS_LABEL,
  DROPDOWN_MAX_HEIGHT,
  POLICY_DIMENSION_LABEL,
  HIERARCHY,
  POLICY_DIMENSION_SELECTALL,
  SELECT_ALL_TEXT,
  DISPLAY_NAME,
  SELECT_ALL
} from '../../../shared/PolicyDialog/constants';
import { getDimensions, getHierarchy } from '../../../store/role/policyDimension/dimension';
import { fetchDataPlatformActions } from '../../../store/role/policy';
import {
  selectDimensionsList,
  selectDimensionPolicyData,
  selectDimensionFormError
} from '../../../store/role/policyDimension/dimensionSelector';
import { selectDataAccessActions } from '../../../store/role/roleSelector';
import {
  setDimensionPolicyData,
  updateDimensionFormError
} from '../../../store/role/policyDimension/dimensionActionCreator';
import { selectError } from '../../../store/error/errorSelector';
import AccordionListCollector from './AccordionListCollector';
import { compareByProperty } from '../../../utility/commonUtility';
import { ISelect, IDimension } from '../../../models/Roles/RolesContracts';
import LuiMultiSelectCustom from '../../../shared/LuiMultiSelectCustom';

const Dimension = (props: any) => {
  const { setIsSaveDisabled } = props;
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const dimensionsList: IDimension[] = useSelector(selectDimensionsList);
  const dataAccessActions = useSelector(selectDataAccessActions);
  const dimensionPolicyData = useSelector(selectDimensionPolicyData);
  const hierarchyLevels = dimensionPolicyData.hierarchy?.levels;
  const formErrorState = useSelector(selectDimensionFormError);
  const errors = useSelector(selectError);

  const dimensionData = useMemo(
    () => dimensionsList.map(dimension => ({ ...dimension, key: dimension.id, displayName: dimension.label })),
    [dimensionsList]
  );

  useEffect(() => {
    getDimensions(dispatch);
    fetchDataPlatformActions(dispatch);
  }, [dispatch]);


  const handleSaveButtonDisable = useCallback(() => {
    if (
      dimensionPolicyData.policyName !== '' &&
      dimensionPolicyData.policyDescription !== '' &&
      dimensionPolicyData.dimension &&
      dimensionPolicyData.hierarchy &&
      (dimensionPolicyData.members || dimensionPolicyData.selectAllDimension) &&
      dimensionPolicyData.action.length !== 0
    ) {
      setIsSaveDisabled(false);
    } else {
      setIsSaveDisabled(true);
    }
  }, [dimensionPolicyData, setIsSaveDisabled]);


  useEffect(() => {
    handleSaveButtonDisable();
  }, [handleSaveButtonDisable]);


  const handleOnChange = (e: { target: { name: string; value: string } }) => {
    dispatch(setDimensionPolicyData(e.target.name, e.target.value));
    dispatch(updateDimensionFormError(e.target.name, ''));
  };

  const onDimensionChange = (evt: any) => {
    dispatch(setDimensionPolicyData(POLICY_DIMENSION, evt.target.value));
    dispatch(setDimensionPolicyData(POLICY_HIERARCHY, null));
    getHierarchy(dispatch, evt.target.value.id)
  };

  const getHierarchyMap = (hierarchyLevels: any[]) => {

    let disabled = false;
    return hierarchyLevels.map((level: any, index: number, levels: any[]) => {
      if (index > 0) {
        // When the previous level dimension has more than one selected item the next level dimesions should be disabled
        disabled = dimensionPolicyData?.members === null ||
          dimensionPolicyData?.members[levels[index - 1]?.id] === undefined ||
          dimensionPolicyData?.members[levels[index - 1]?.id][0]?.value === '' ||
          dimensionPolicyData?.members[levels[index - 1]?.id][0]?.value === null ||
          dimensionPolicyData?.members[levels[index - 1]?.id]?.length !== 1;
      }
      return renderAccordionListCollector(level, index, disabled);

    })
  };

  const renderAccordionListCollector = (level: any, index: number, disabled: boolean) => {
    const props = {
      ...level,
      index,
      disabled: dimensionPolicyData.selectAllDimension || disabled,
      dimensionId: dimensionPolicyData.dimension.id
    };

    return (
      <AccordionListCollector {...props} />
    )
  };

  const handleSelectAll = (event: { target: { name: string; value: any; checked: any } }) => {
    dispatch(setDimensionPolicyData(event.target.name, !dimensionPolicyData.selectAllDimension));
    dispatch(updateDimensionFormError(event.target.name, ''));
  };
  const handleActionChange = (name: string, e: ISelect[]) => {
    dispatch(setDimensionPolicyData(name, e));
    dispatch(updateDimensionFormError(name, ''));
  };
  return (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid item xs={5} className={classes.formStyle}>
        <TextField
          id="policyName"
          variant='standard'
          name={POLICY_NAME}
          label={POLICY_NAME_LABEL}
          className={classes.textFieldSpacing}
          fullWidth
          value={dimensionPolicyData.policyName}
          onChange={handleOnChange}
          error={formErrorState.policyName !== ''}
          helperText={formErrorState.policyName}
          required
        />
      </Grid>
      <Grid item xs={6} className={classes.formStyle}>
        <TextField
          id="policyDescription"
          variant='standard'
          name={POLICY_DESCRIPTION}
          label={POLICY_DESC_LABEL}
          className={classes.textFieldSpacing}
          fullWidth
          value={dimensionPolicyData.policyDescription}
          onChange={handleOnChange}
          error={formErrorState.policyDescription !== ''}
          helperText={formErrorState.policyDescription}
          required
        />
      </Grid>
      <Grid item xs={6} className={classes.formStyle}>
        <LuiSelect
          maxHeight={DROPDOWN_MAX_HEIGHT}
          id="dimension-dropdown"
          onSelectionChange={onDimensionChange}
          itemData={dimensionData}
          inputLabel={POLICY_DIMENSION_LABEL}
          selectedItem={dimensionPolicyData.dimension}
          width={300}
        />
      </Grid>
      {dimensionPolicyData.dimension !== null && <Grid item xs={12} className={classes.formStyle}>
        {dimensionPolicyData.hierarchy !== null ?
          (<Typography>{`${HIERARCHY}: ${dimensionPolicyData.hierarchy.id}`}</Typography>) : ''}
      </Grid>}
      {dimensionPolicyData.hierarchy !== null &&
        <Box className={classes.fullWidthFlex}>
          <Grid item xs={12} className={classes.formStyle}>
            <FormControlLabel
              control={
                <Checkbox
                  id={POLICY_DIMENSION_SELECTALL}
                  name={POLICY_DIMENSION_SELECTALL}
                  checked={dimensionPolicyData.selectAllDimension}
                  onChange={handleSelectAll}
                  size="small"
                />
              }
              label={`${SELECT_ALL_TEXT} ${dimensionPolicyData.dimension?.label}`}
            ></FormControlLabel>
          </Grid>
          {hierarchyLevels?.length > 0 && getHierarchyMap(hierarchyLevels)}
          <Grid item xs={6} className={classes.formStyle}>
            <LuiMultiSelectCustom
              id="actions-multi-select"
              itemData={dataAccessActions?.sort(compareByProperty(DISPLAY_NAME))}
              itemKey="id"
              itemDisplayName="displayName"
              inputLabel={POLICY_ACTIONS_LABEL}
              selectedItems={dimensionPolicyData?.action}
              selectAll
              selectAllDisplayText={SELECT_ALL}
              maxHeight={DROPDOWN_MAX_HEIGHT}
              onSelectionChange={(e: any) => handleActionChange('action', e)}
            />
          </Grid>
        </Box>
      }

      {errors && <ErrorMessage {...errors} />}
    </Grid>
  );
}

export default Dimension;